import axios from 'axios';
import { API_URL } from '../config/api';

export const RIDES_API_URL = API_URL;

export type RideCategory = 'AUTO' | 'BIKE' | 'CAB' | 'VAN' | 'SHARED_RIDE';
export type RideBookingType = 'INSTANT' | 'SCHEDULED' | 'RECURRING' | 'STAND' | 'SUPPORT_ASSISTED';
export type RidePricingMode = 'FIXED' | 'METER' | 'PLATFORM_ESTIMATE' | 'DRIVER_QUOTE' | 'STAND_FARE';
export type PaymentMode = 'CASH' | 'UPI' | 'QR' | 'ONLINE' | 'WALLET';
export type StandType =
  | 'AUTO_STAND'
  | 'BIKE_STAND'
  | 'CAB_STAND'
  | 'SHARED_AUTO_POINT'
  | 'VILLAGE_TRANSPORT_POINT'
  | 'SCHOOL_TRANSPORT_POINT'
  | 'HOSPITAL_TRANSPORT_POINT'
  | 'EVENT_TEMPORARY_STAND';
export type StandQueueModel = 'FIFO' | 'MANUAL' | 'OPEN_ACCEPTANCE' | 'ROUTE_BASED' | 'PRIORITY';
export type DriverVerificationStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'MORE_INFORMATION_REQUIRED'
  | 'APPROVED'
  | 'REJECTED'
  | 'SUSPENDED'
  | 'EXPIRED_DOCUMENTS'
  | 'BLOCKED';
export type VehicleStatus = 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'DOCUMENT_EXPIRED' | 'UNDER_MAINTENANCE' | 'SUSPENDED' | 'BLOCKED';

export type RideDriverPlan = {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billingCycle: 'monthly' | 'quarterly';
  validityDays: number;
  features: string[];
};

export type RideLocation = {
  address: string;
  landmark?: string;
  village?: string;
  mandal?: string;
  district?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
};

export type EstimateRidePayload = {
  category: RideCategory;
  pickup: RideLocation;
  destination: RideLocation;
  pricingMode?: RidePricingMode;
};

export type CreateRidePayload = EstimateRidePayload & {
  bookingType?: RideBookingType;
  bookedFor?: {
    name: string;
    phone: string;
  };
  bookedForRelationship?: string;
  standId?: string;
  scheduledAt?: string;
  paymentMode?: PaymentMode;
  passengerNote?: string;
  passengerCount?: number;
};

export type SubmitRideQuotePayload = {
  fare: number;
  message?: string;
  validUntil?: string;
};

export type RegisterRideDriverPayload = {
  fullName: string;
  mobileNumber: string;
  alternateMobileNumber?: string;
  categories: RideCategory[];
  currentAddress: RideLocation;
  preferredLanguage?: string;
  serviceAreas?: string[];
  standId?: string;
  upiId?: string;
  licenceNumber?: string;
  identityDocument?: string;
  licenceDocumentUrl?: string;
  identityDocumentUrl?: string;
  vehicleRcDocumentUrl?: string;
  vehicleInsuranceDocumentUrl?: string;
  permitDocumentUrl?: string;
  planId?: string;
};

export type CreateRideVehiclePayload = {
  vehicleType: RideCategory;
  registrationNumber: string;
  manufacturer?: string;
  model?: string;
  year?: number;
  fuelType?: string;
  color?: string;
  seatingCapacity?: number;
  permitType?: string;
  acAvailable?: boolean;
  accessibilitySupport?: boolean;
};

export type CreateRideStandPayload = {
  name: string;
  type: StandType;
  location: RideLocation;
  landmark?: string;
  contactPerson: string;
  contactNumber: string;
  queueModel?: StandQueueModel;
  approximateDriverCount?: number;
  rules?: string;
};

const getAuthHeader = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const token = user?.accessToken || user?.user?.accessToken;
  return token
    ? {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    : {
        'Content-Type': 'application/json',
      };
};

const getMultipartAuthHeader = () => {
  const headers = { ...getAuthHeader() };
  delete (headers as Record<string, string>)['Content-Type'];
  return headers;
};

class RidesService {
  overview() {
    return axios.get(`${API_URL}/rides/overview`).then((response) => response.data);
  }

  estimate(payload: EstimateRidePayload) {
    return axios.post(`${API_URL}/rides/estimate`, payload).then((response) => response.data);
  }

  createRide(payload: CreateRidePayload) {
    return axios.post(`${API_URL}/rides/authenticated`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  myRides() {
    return axios.get(`${API_URL}/rides/me/history`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  ride(rideId: string) {
    return axios.get(`${API_URL}/rides/${rideId}`).then((response) => response.data);
  }

  stands(q?: string) {
    const params = q ? { q } : {};
    return axios.get(`${API_URL}/rides/stands`, { params }).then((response) => response.data);
  }

  registerDriver(payload: RegisterRideDriverPayload) {
    return axios.post(`${API_URL}/rides/drivers/register`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  driverPlans() {
    return axios.get(`${API_URL}/rides/drivers/plans`).then((response) => response.data as RideDriverPlan[]);
  }

  uploadDriverDocuments(files: Partial<Record<'licenceDocument' | 'identityDocument' | 'vehicleRcDocument' | 'vehicleInsuranceDocument' | 'permitDocument', File>>) {
    const formData = new FormData();
    Object.entries(files).forEach(([key, file]) => {
      if (file) formData.append(key, file);
    });
    return axios.post(`${API_URL}/rides/drivers/documents`, formData, { headers: getMultipartAuthHeader() }).then((response) => response.data);
  }

  myDriverProfile() {
    return axios.get(`${API_URL}/rides/drivers/me`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  subscribeDriverPlan(payload: { planId: string; paymentMethodId?: 'WALLET' }) {
    return axios.post(`${API_URL}/rides/drivers/me/subscribe`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  walletBalance() {
    return axios.get(`${API_URL}/teachers/wallet/balance`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  updateAvailability(payload: {
    isOnline: boolean;
    isAvailable?: boolean;
    currentLocation?: RideLocation;
    availabilityNote?: string;
    availabilityValidUntil?: string;
  }) {
    return axios.patch(`${API_URL}/rides/drivers/me/availability`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  driverRequests() {
    return axios.get(`${API_URL}/rides/drivers/me/requests`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  driverNotificationConfig() {
    return axios.get(`${API_URL}/rides/driver-notifications/config`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  subscribeDriverNotifications(payload: { endpoint: string; keys: { p256dh: string; auth: string }; userAgent?: string }) {
    return axios.post(`${API_URL}/rides/driver-notifications/subscribe`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  unsubscribeDriverNotifications(endpoint: string) {
    return axios.post(`${API_URL}/rides/driver-notifications/unsubscribe`, { endpoint }, { headers: getAuthHeader() }).then((response) => response.data);
  }

  createVehicle(payload: CreateRideVehiclePayload) {
    return axios.post(`${API_URL}/rides/vehicles`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  createStand(payload: CreateRideStandPayload) {
    return axios.post(`${API_URL}/rides/stands`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  acceptRide(rideId: string) {
    return axios.post(`${API_URL}/rides/${rideId}/accept`, {}, { headers: getAuthHeader() }).then((response) => response.data);
  }

  submitQuote(rideId: string, payload: SubmitRideQuotePayload) {
    return axios.post(`${API_URL}/rides/${rideId}/quote`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  startRide(rideId: string, otp: string) {
    return axios.post(`${API_URL}/rides/${rideId}/start`, { otp }, { headers: getAuthHeader() }).then((response) => response.data);
  }

  completeRide(rideId: string, payload: { finalFare: number; paymentMode: PaymentMode; paymentStatus?: string }) {
    return axios.post(`${API_URL}/rides/${rideId}/complete`, payload, { headers: getAuthHeader() }).then((response) => response.data);
  }

  cancelRide(rideId: string, reason?: string) {
    return axios.post(`${API_URL}/rides/${rideId}/cancel`, { reason }, { headers: getAuthHeader() }).then((response) => response.data);
  }

  adminOverview() {
    return axios.get(`${API_URL}/admin/rides/overview`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  adminRides() {
    return axios.get(`${API_URL}/admin/rides`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  adminDrivers() {
    return axios.get(`${API_URL}/admin/rides/drivers`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  adminVehicles() {
    return axios.get(`${API_URL}/admin/rides/vehicles`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  adminStands() {
    return axios.get(`${API_URL}/admin/rides/stands`, { headers: getAuthHeader() }).then((response) => response.data);
  }

  reviewDriver(driverId: string, verificationStatus: DriverVerificationStatus, adminRemarks?: string) {
    return axios.patch(`${API_URL}/admin/rides/drivers/${driverId}/review`, { verificationStatus, adminRemarks }, { headers: getAuthHeader() }).then((response) => response.data);
  }

  reviewVehicle(vehicleId: string, status: VehicleStatus) {
    return axios.patch(`${API_URL}/admin/rides/vehicles/${vehicleId}/review`, { status }, { headers: getAuthHeader() }).then((response) => response.data);
  }

  reviewStand(standId: string, verificationStatus: DriverVerificationStatus, adminRemarks?: string) {
    return axios.patch(`${API_URL}/admin/rides/stands/${standId}/review`, { verificationStatus, adminRemarks }, { headers: getAuthHeader() }).then((response) => response.data);
  }
}

const ridesService = new RidesService();
export default ridesService;
